import * as React from 'react';
import { connect } from 'react-redux';

import { createShapeFile, fetchShapeFileName } from '@actions/gis';
import { getSignedURL, S3_SIGNED_URL_SUCCESS } from '@actions/s3';
import { getS3URL } from '@utils/AppUtil';
import { s3uploadFile } from '@utils/S3Util';

import './style.scss';

type CDBGUploadStatus = 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE' | null;

interface ICDBGProps {
  shapefile: string;
  cdbgUploadStatus: CDBGUploadStatus;
  fetchShapeFileName: TCreateShapeFileName;
  createShapeFile: (fileURL: string) => Promise<any>;
  getSignedURL: TGetSignedURL;
}

interface ICDBGState {
  isUploading: boolean;
  uploadError: string | null;
}

const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds

class CDBG extends React.Component<ICDBGProps, ICDBGState> {
  private pollInterval: NodeJS.Timeout | null = null;

  constructor(props: ICDBGProps) {
    super(props);

    this.state = {
      isUploading: false,
      uploadError: null,
    };
  }

  componentDidMount() {
    this.props.fetchShapeFileName();
    // If component mounts while processing is in progress, resume polling
    if (this.props.cdbgUploadStatus === 'IN_PROGRESS') {
      this.startPolling();
    }
  }

  componentDidUpdate(prevProps: ICDBGProps) {
    // Handle status changes
    if (prevProps.cdbgUploadStatus !== this.props.cdbgUploadStatus) {
      const { cdbgUploadStatus } = this.props;

      if (cdbgUploadStatus === 'SUCCESS') {
        this.stopPolling();
        this.setState({ isUploading: false, uploadError: null });
      } else if (cdbgUploadStatus === 'FAILURE') {
        this.stopPolling();
        this.setState({
          isUploading: false,
          uploadError: 'Processing failed. Please try uploading again.',
        });
      }
    }
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  startPolling = () => {
    if (this.pollInterval) {
      return; // Already polling
    }

    this.pollInterval = setInterval(async () => {
      try {
        await this.props.fetchShapeFileName();
      } catch (error) {
        // Continue polling on error
        console.error('Error fetching CDBG status:', error);
      }
    }, POLL_INTERVAL_MS);
  };

  stopPolling = () => {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  };

  handleAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList = e.target.files;
    const shapefile = files ? Array.from(files)[0] : null;

    // Reset file input
    (e.target as any).value = null;

    if (shapefile) {
      this.setState({ isUploading: true, uploadError: null });

      try {
        // Step 1: Get signed URL from backend
        // Use agencyId 0 for CDBG as it's a global file (not agency-specific)
        const action = await this.props.getSignedURL(
          0,
          S3Operation.CDBG_FILE,
          shapefile.name
        );

        if (action.type === S3_SIGNED_URL_SUCCESS) {
          // Step 2: Upload directly to S3
          const body = new Map(action.response.requestBodyparams);
          const res = await s3uploadFile(body, shapefile, null, false);
          const s3URL = getS3URL(res);

          // Step 3: Tell backend to start processing the S3 URL
          // This returns immediately with 202 Accepted
          await this.props.createShapeFile(s3URL);

          // Step 4: Start polling for status updates
          this.startPolling();
          // Immediately fetch to get the IN_PROGRESS status
          this.props.fetchShapeFileName();
        }
      } catch (error) {
        this.setState({
          isUploading: false,
          uploadError: 'Failed to upload file. Please try again.',
        });
      }
    }
  };

  renderSecondaryHeader = () => (
    <div className="cdbg__header">
      <h2>CDBG</h2>
    </div>
  );

  renderUploadStatus = () => {
    const { cdbgUploadStatus } = this.props;
    const { isUploading } = this.state;

    if (isUploading || cdbgUploadStatus === 'IN_PROGRESS') {
      return (
        <div className="cdbg__file-upload cdbg__file-upload--uploading">
          <label>Processing file... This may take several minutes.</label>
        </div>
      );
    }

    return null;
  };

  renderCDBGContainer = () => {
    const { shapefile, cdbgUploadStatus } = this.props;
    const { isUploading, uploadError } = this.state;

    const isProcessing = isUploading || cdbgUploadStatus === 'IN_PROGRESS';

    return (
      <div className="cdbg__container tile-base">
        <h2>CDBG Shape File</h2>
        <label className="cdbg__disclaimer">This shape file is used for all agencies.</label>
        {!isProcessing ? (
          <div className="cdbg__file-upload">
            {shapefile && [
              <label key={1}>
                <b>{shapefile}</b>
              </label>,
              <span key={2}>|</span>,
            ]}
            <label htmlFor="file-upload" className="cdbg__file-label">
              {shapefile ? 'Replace File' : 'Upload File'}
            </label>
            <input
              id="file-upload"
              type="file"
              accept=".zip"
              style={{ display: 'none' }}
              onChange={this.handleAttachmentChange}
              multiple={false}
            />
          </div>
        ) : (
          this.renderUploadStatus()
        )}
        {uploadError && <span className="cdbg__error">{uploadError}</span>}
      </div>
    );
  };

  render() {
    return (
      <div className="cdbg">
        {this.renderSecondaryHeader()}
        {this.renderCDBGContainer()}
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  shapefile: state.gis.shapefile,
  cdbgUploadStatus: state.gis.cdbgUploadStatus,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchShapeFileName: () => dispatch(fetchShapeFileName()),
  createShapeFile: (fileURL: string) => dispatch(createShapeFile(fileURL)),
  getSignedURL: (agencyId: number, operation: S3Operation, fileName: string) =>
    dispatch(getSignedURL(agencyId, operation, fileName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CDBG);
